import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ViewControls = ({ 
  viewMode, 
  onViewModeChange, 
  sortBy, 
  onSortChange, 
  itemsPerPage, 
  onItemsPerPageChange,
  totalItems,
  itemType, // NEW
  selectedItems,
  onSelectAll,
  onClearSelection,
  onBulkAction,
  lastUpdatedTime
}) => {
  const [timeAgo, setTimeAgo] = useState('just now');

  useEffect(() => {
    if (!lastUpdatedTime) return;

    const updateLabel = () => {
      const diffMs = Date.now() - new Date(lastUpdatedTime).getTime();
      const diffSecs = Math.max(0, Math.floor(diffMs / 1000));
      const diffMins = Math.floor(diffSecs / 60);

      if (diffSecs < 10) {
        setTimeAgo('just now');
      } else if (diffSecs < 60) {
        setTimeAgo(`${diffSecs}s ago`);
      } else if (diffMins === 1) {
        setTimeAgo('1 minute ago');
      } else {
        setTimeAgo(`${diffMins} minutes ago`);
      }
    };

    updateLabel();
    const interval = setInterval(updateLabel, 5000); // Update every 5 seconds to feel incredibly responsive!
    return () => clearInterval(interval);
  }, [lastUpdatedTime]);

  const sortOptions = [
    { value: 'relevance', label: 'Relevance' },
    { value: 'popularity', label: 'Most Popular' },
    { value: 'rating', label: 'Highest Rated' },
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    ...(itemType === 'components' ? [
      { value: 'price-low', label: 'Price: Low to High' },
      { value: 'price-high', label: 'Price: High to Low' },
    ] : []),
    { value: 'name-asc', label: 'Name: A to Z' },
    { value: 'name-desc', label: 'Name: Z to A' },
    { value: 'downloads', label: 'Most Downloaded' },
    { value: 'filesize-small', label: 'File Size: Small to Large' },
    { value: 'filesize-large', label: 'File Size: Large to Small' }
  ];

  const itemsPerPageOptions = [
    { value: 12, label: '12 per page' },
    { value: 24, label: '24 per page' },
    { value: 48, label: '48 per page' },
    { value: 96, label: '96 per page' }
  ];

  const bulkActions = [
    ...(itemType === 'components' ? [
      { value: 'add-to-cart', label: 'Add to Cart', icon: 'ShoppingCart' },
    ] : []),
    { value: 'add-to-wishlist', label: 'Add to Wishlist', icon: 'Heart' },
    { value: 'compare', label: 'Compare Models', icon: 'GitCompare' }
  ];

  const handleBulkActionSelect = (action) => {
    onBulkAction(action, selectedItems);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        {/* Left Section - Results Info and Selection */}
        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
          <div className="text-sm text-muted-foreground">
            Showing <span className="font-medium text-foreground">{totalItems?.toLocaleString()}</span> {itemType}
          </div>
          
          {selectedItems?.length > 0 && (
            <div className="flex items-center space-x-2">
              <div className="text-sm text-primary font-medium">
                {selectedItems?.length} selected
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearSelection}
                className="text-muted-foreground hover:text-foreground"
              >
                Clear
              </Button>
            </div>
          )}
        </div>

        {/* Right Section - Controls */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
          {/* Select Actions */}
          {selectedItems?.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">Bulk actions:</span>
              <div className="flex space-x-1">
                {bulkActions?.map((action) => (
                  <Button
                    key={action?.value}
                    variant="outline"
                    size="sm"
                    onClick={() => handleBulkActionSelect(action?.value)}
                    iconName={action?.icon}
                    className="whitespace-nowrap"
                  >
                    {action?.label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Select All */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onSelectAll}
            iconName="CheckSquare"
            className="whitespace-nowrap"
          >
            Select All
          </Button>

          {/* Sort By */}
          <Select
            options={sortOptions}
            value={sortBy}
            onChange={onSortChange}
            className="w-full sm:w-48"
          />

          {/* Items Per Page */}
          <Select
            options={itemsPerPageOptions}
            value={itemsPerPage}
            onChange={onItemsPerPageChange}
            className="w-full sm:w-36"
          />

          {/* View Mode Toggle */}
          <div className="flex items-center bg-muted rounded-lg p-1">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => onViewModeChange('grid')}
              iconName="Grid3X3"
              className="rounded-md"
            />
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => onViewModeChange('list')}
              iconName="List"
              className="rounded-md"
            />
            <Button
              variant={viewMode === 'component-list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => onViewModeChange('component-list')}
              iconName="Rows" // Using 'Rows' icon for component list view
              className="rounded-md"
            />
          </div>
        </div>
      </div>
      {/* Advanced Filters Toggle */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            iconName="Filter"
            iconPosition="left"
          >
            Advanced Filters
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconName="Save"
            iconPosition="left"
          >
            Save Search
          </Button>
        </div>

        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Clock" size={14} />
          <span>Last updated: {timeAgo}</span>
        </div>
      </div>
    </div>
  );
};

export default ViewControls;